import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { BUCKET_STYLES, LEVEL_STYLES } from "./constants";
import { Clock, ChartLine, Bank, Path, CurrencyCircleDollar, MapPin, Lightbulb } from "@phosphor-icons/react";

const Section = ({ icon: Icon, title, children }) => (
  <div className="border-t border-slate-200 pt-5">
    <p className="overline-label text-slate-400 flex items-center gap-2"><Icon size={14} /> {title}</p>
    <div className="mt-2.5">{children}</div>
  </div>
);

export const CountryDetail = ({ country, bucketName, open, onClose }) => {
  if (!country) return null;
  const style = BUCKET_STYLES[country.bucket];
  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent data-testid="country-detail-sheet" className="w-full sm:max-w-xl overflow-y-auto bg-white p-0">
        <div className="p-6 border-b border-slate-200 sticky top-0 bg-white/90 backdrop-blur-xl z-10" style={{ borderTop: `3px solid ${style.color}` }}>
          <SheetHeader className="text-left space-y-0">
            <div className="flex items-center gap-3">
              <span className="text-3xl">{country.flag}</span>
              <div>
                <SheetTitle className="font-heading font-extrabold text-2xl tracking-tight">{country.name}</SheetTitle>
                <SheetDescription asChild>
                  <span className={`inline-block mt-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-1 ${style.badge}`}>{bucketName}</span>
                </SheetDescription>
              </div>
              <div className="ml-auto text-right">
                <p className="overline-label text-slate-400">MAI Readiness</p>
                <p className="font-heading font-extrabold text-3xl tracking-tight" style={{ color: style.color }}>{country.mai_readiness}</p>
              </div>
            </div>
          </SheetHeader>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div className="border border-slate-200 p-4">
              <p className="overline-label text-slate-400 flex items-center gap-1.5"><Clock size={13} /> Review Timeline</p>
              <p className="font-heading font-bold text-xl mt-1.5">{country.review_timeline_months} months</p>
            </div>
            <div className="border border-slate-200 p-4">
              <p className="overline-label text-slate-400 flex items-center gap-1.5"><ChartLine size={13} /> Market Size</p>
              <p className="text-sm text-slate-700 mt-1.5 leading-snug">{country.market_size}</p>
            </div>
          </div>

          <Section icon={Bank} title="HTA Body">
            <p className="text-sm text-slate-700 leading-relaxed">{country.hta_body}</p>
            <p className="text-sm text-slate-500 mt-1.5"><span className="font-semibold text-slate-700">Authority:</span> {country.reimbursement_authority}</p>
          </Section>

          <Section icon={Path} title="Reimbursement Pathway">
            <p className="text-sm text-slate-700 leading-relaxed">{country.reimbursement_pathway}</p>
          </Section>

          <Section icon={CurrencyCircleDollar} title="Pricing Mechanism">
            <p className="text-sm text-slate-700 leading-relaxed">{country.pricing_mechanism}</p>
          </Section>

          <Section icon={ChartLine} title="Evidence Requirements">
            <div className="border border-slate-200 divide-y divide-slate-200">
              {country.evidence_requirements.map((req) => (
                <div key={req.type} className="p-3.5 flex gap-3">
                  <span className={`shrink-0 h-fit text-[10px] uppercase tracking-wider font-semibold px-2 py-1 ${LEVEL_STYLES[req.level]}`}>{req.level}</span>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{req.type}</p>
                    <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{req.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          <Section icon={MapPin} title="Local Data Preferences">
            <ul className="space-y-2">
              {country.local_data_preferences.map((pref) => (
                <li key={pref} className="text-sm text-slate-700 flex gap-2.5">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0" style={{ background: style.color }} />
                  {pref}
                </li>
              ))}
            </ul>
          </Section>

          <Section icon={ChartLine} title="Reimbursement Status">
            <p className="text-sm text-slate-700 leading-relaxed">{country.access_status}</p>
          </Section>

          {country.brand_access && (
            <Section icon={Lightbulb} title="Brand vs Generic Access Pathway">
              <div data-testid="brand-access-panel" className="border border-slate-200 p-4 bg-slate-50">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 ${
                    country.brand_access.pathway === "brand_specific" ? "bg-emerald-600 text-white" :
                    country.brand_access.pathway === "generic_name" ? "bg-slate-900 text-white" : "bg-amber-500 text-white"
                  }`}>{country.brand_access.pathway.replace(/_/g, " ")}</span>
                  {country.brand_access.brand_opportunity && (
                    <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 border border-emerald-300 text-emerald-700 bg-emerald-50">Brand opportunity</span>
                  )}
                </div>
                <p className="text-sm text-slate-700 leading-relaxed">{country.brand_access.detail}</p>
              </div>
            </Section>
          )}

          {country.competitive_landscape?.competitors?.length > 0 && (
            <Section icon={ChartLine} title="Competitive Landscape">
              <div data-testid="competitive-panel" className="space-y-2.5">
                {country.competitive_landscape.note && (
                  <p className="text-xs text-slate-500 leading-relaxed">{country.competitive_landscape.note}</p>
                )}
                {country.competitive_landscape.competitors.map((c, i) => (
                  <div key={i} className="border border-slate-200 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-semibold text-slate-800">{c.competitor} <span className="text-slate-400 font-normal">· {c.company}</span></p>
                      {c.pricing_anchor && <span className="shrink-0 text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200">Pricing anchor</span>}
                    </div>
                    <p className="text-[11px] text-slate-500 mt-0.5">{c.status}</p>
                  </div>
                ))}
              </div>
            </Section>
          )}

          <Section icon={Lightbulb} title="Strategic Considerations">
            <p className="text-sm text-slate-700 leading-relaxed bg-slate-50 border border-slate-200 p-4">{country.key_considerations}</p>
          </Section>
        </div>
      </SheetContent>
    </Sheet>
  );
};
